import {
  httpGetGroupByGroupId,
  httpGetMergedCurrent,
  httpGetSearchViewByViewIdGroupIds,
  httpGetSystemCurrent,
  httpGetTeamByTeamId,
  httpGetUserByUserId,
  httpPatchGroupByGroupId,
  httpPatchSystemCurrent,
  httpPatchTeamByTeamId,
  httpPatchUserByUserId,
  httpPutSearchViewByViewIdGroupIds,
  type SettingsGroupSettingPublicSchemaPartial,
  type SettingsMergedSettingsSchemaNullable,
  type SettingsSearchDisplayFieldSchema,
  type SettingsSystemSettingPublicSchemaPartial,
  type SettingsUserSettingSchemaPartial,
} from '@iconik-io/iconik-api/src/services/settings';

import { createAppThunk } from '../types';
import {
  setSettingsLoading,
  updateGroupSettings,
  updateMergedSettings,
  updateSystemSettings,
  updateUserSettings,
} from './slice';

/**
 * Resolves either way so a settings outage cannot break app start, which leaves
 * empty settings indistinguishable from settings that are genuinely off. It
 * resolves to `undefined` when the settings could not be read, which is how a
 * caller that must not run on defaults — the share pages, whose DRM and
 * watermark gates read these settings — tells the two apart.
 */
export const getMergedSettings = createAppThunk(
  (callback?: (setting: SettingsMergedSettingsSchemaNullable) => void) =>
    (dispatch): Promise<SettingsMergedSettingsSchemaNullable | undefined> => {
      dispatch(setSettingsLoading({ type: 'merged', loading: true }));
      return dispatch(httpGetMergedCurrent())
        .then((response) => {
          const settings = response.body;

          if (callback && typeof callback === 'function') {
            callback(settings);
          }

          dispatch(updateMergedSettings({ settings }));
          return settings;
        })
        .catch(() => {
          dispatch(
            updateMergedSettings({
              settings: {},
            }),
          );
          return undefined;
        })
        .finally(() => {
          dispatch(setSettingsLoading({ type: 'merged', loading: false }));
        });
    },
);

export const getUserSettings = createAppThunk(
  (userId: string) => (dispatch) => {
    dispatch(setSettingsLoading({ type: 'user', loading: true }));
    return dispatch(httpGetUserByUserId(userId))
      .then((response) =>
        dispatch(updateUserSettings({ userId, settings: response.body })),
      )
      .catch(() =>
        dispatch(
          updateUserSettings({
            userId,
            settings: {},
          }),
        ),
      )
      .finally(() => {
        dispatch(setSettingsLoading({ type: 'user', loading: false }));
      });
  },
);

export const getGroupSettings = createAppThunk(
  (groupId: string) => (dispatch) => {
    dispatch(setSettingsLoading({ type: 'group', loading: true }));
    return dispatch(httpGetGroupByGroupId(groupId))
      .then((response) => {
        dispatch(
          updateGroupSettings({
            groupId,
            settings: response.body,
          }),
        );
      })
      .finally(() => {
        dispatch(setSettingsLoading({ type: 'group', loading: false }));
      });
  },
);

export const getTeamSettings = createAppThunk(
  (groupId: string) => (dispatch) => {
    dispatch(setSettingsLoading({ type: 'group', loading: true }));
    return dispatch(httpGetTeamByTeamId(groupId))
      .then((response) => {
        dispatch(
          updateGroupSettings({
            groupId,
            settings: response.body,
          }),
        );
      })
      .finally(() => {
        dispatch(setSettingsLoading({ type: 'group', loading: false }));
      });
  },
);

export const getSystemSettings = createAppThunk(() => (dispatch) => {
  dispatch(setSettingsLoading({ type: 'system', loading: true }));
  return dispatch(httpGetSystemCurrent())
    .then(({ body }) => {
      const settings = body;
      return dispatch(updateSystemSettings({ settings }));
    })
    .catch(() =>
      dispatch(
        updateSystemSettings({
          settings: {},
        }),
      ),
    )
    .finally(() => {
      dispatch(setSettingsLoading({ type: 'system', loading: false }));
    });
});

export const modifySystemSettings = createAppThunk(
  (data: SettingsSystemSettingPublicSchemaPartial) => (dispatch) =>
    dispatch(httpPatchSystemCurrent(data)).then((response) => {
      dispatch(updateSystemSettings({ settings: response.body }));
      return dispatch(getMergedSettings());
    }),
);

export const modifyUserSettings = createAppThunk(
  (userId: string, data: SettingsUserSettingSchemaPartial) => (dispatch) => {
    return dispatch(httpPatchUserByUserId(userId, data)).then((response) => {
      dispatch(updateUserSettings({ userId, settings: response.body }));
      return dispatch(getMergedSettings());
    });
  },
);

type NormalizedRequestData = Record<string, unknown>;

const normalizeSettingsRequest = (
  data: SettingsGroupSettingPublicSchemaPartial,
) => {
  const requestData: NormalizedRequestData = { ...data };
  if (requestData.default_upload_storage_id === '') {
    requestData.default_upload_storage_id = null;
  }
  Object.keys(requestData).forEach((key) => {
    if (requestData[key] === undefined) {
      requestData[key] = null;
    }
  });
  return requestData;
};

export const modifyGroupSettingsRequest = (
  groupId: string,
  data: SettingsGroupSettingPublicSchemaPartial,
) => {
  const requestData = normalizeSettingsRequest(data);
  return httpPatchGroupByGroupId(groupId, requestData);
};

export const modifyGroupSettings = createAppThunk(
  (groupId: string, data: SettingsGroupSettingPublicSchemaPartial) =>
    (dispatch) =>
      dispatch(modifyGroupSettingsRequest(groupId, data)).then((response) => {
        dispatch(
          updateGroupSettings({
            groupId,
            settings: response.body,
          }),
        );
        return dispatch(getMergedSettings());
      }),
);

export const modifyTeamSettingsRequest = (
  groupId: string,
  data: SettingsGroupSettingPublicSchemaPartial,
) => {
  const requestData = normalizeSettingsRequest(data);
  return httpPatchTeamByTeamId(groupId, requestData);
};

export const modifyTeamSettings = createAppThunk(
  (groupId: string, data: SettingsGroupSettingPublicSchemaPartial) =>
    (dispatch) =>
      dispatch(modifyTeamSettingsRequest(groupId, data)).then((response) => {
        dispatch(
          updateGroupSettings({
            groupId,
            settings: response.body,
          }),
        );
        return dispatch(getMergedSettings());
      }),
);

export const updateCurrentUserVisibleSearchFields = createAppThunk(
  (
    userId: string | undefined,
    fields: SettingsSearchDisplayFieldSchema[],
    mergedSettings?: SettingsMergedSettingsSchemaNullable,
  ) =>
    (dispatch) => {
      if (!userId) {
        // If user doesn't exist just update in-memory display field settings,
        // it's probably just a share page
        dispatch(
          updateMergedSettings({
            settings: {
              ...mergedSettings,
              search_display_fields: fields,
            },
          }),
        );
        return Promise.resolve();
      }
      return dispatch(
        modifyUserSettings(userId, { search_display_fields: fields }),
      );
    },
);

export const getGroupsBySearchViewSetting = createAppThunk(
  (viewId: string) => (dispatch) => {
    return dispatch(httpGetSearchViewByViewIdGroupIds(viewId)).then(
      (response) => response.body.object_ids,
    );
  },
);

export const updateSearchViewSettingForGroups = createAppThunk(
  (viewId: string, groupIds: string[]) => (dispatch) => {
    return dispatch(
      httpPutSearchViewByViewIdGroupIds(viewId, { object_ids: groupIds }),
    ).then((response) => response.body);
  },
);
